import axios from "axios";
import { getUserId } from "@/lib/userId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({
  baseURL: API,
  timeout: 60000,
  // Required for the auth session_token cookie to be sent on every API
  // call (Emergent-managed Google auth, iter-42). Per the auth playbook
  // — without `withCredentials`, browsers will silently drop the cookie
  // and every authenticated endpoint will fall back to anonymous mode.
  withCredentials: true,
});
http.interceptors.request.use((cfg) => {
  // Anonymous client id — kept as a fallback so anonymous users still
  // have stable progress. When the user is authenticated the backend's
  // current_user_id dependency reads the cookie first and ignores this.
  cfg.headers["X-User-Id"] = getUserId();
  return cfg;
});

// ─── Auth ────────────────────────────────────────────────────────────────
export const fetchMe = async () => (await http.get("/auth/me")).data;
export const exchangeSession = async (session_id, anonymous_user_id) =>
  (await http.post("/auth/session", { session_id, anonymous_user_id })).data;
export const exchangeGoogleCode = async (code, redirect_uri, anonymous_user_id) =>
  (await http.post("/auth/google", { code, redirect_uri, anonymous_user_id })).data;
export const logout = async () => (await http.post("/auth/logout")).data;

export const fetchStats = async () => (await http.get("/stats")).data;
export const fetchKanaOrigins = async () =>
  (await http.get("/kana-origins")).data?.items || {};

export const fetchKanjiList = async ({ level = "ALL", q = "", limit = 200 } = {}) =>
  (await http.get("/kanji", { params: { level, q, limit } })).data;

export const fetchRadicals = async ({ min_kanji = 3, per_radical = 24 } = {}) =>
  (await http.get("/radicals", { params: { min_kanji, per_radical } })).data;

export const fetchRadicalFull = async (ch) =>
  (await http.get(`/radical/${encodeURIComponent(ch)}`)).data;

export const fetchPhoneticAtlas = async () =>
  (await http.get("/phonetic-atlas")).data;

export const fetchKanjiDetail = async (ch) =>
  (await http.get(`/kanji/${encodeURIComponent(ch)}`)).data;

export const fetchPath = async (level = "N5") =>
  (await http.get("/path", { params: { level } })).data;

export const fetchAtom = async (ch, depth_limit = 3) =>
  (await http.get(`/atom/${encodeURIComponent(ch)}`, { params: { depth_limit } })).data;

export const fetchOnyomiReading = async (reading, group_by_phonetic = true) =>
  (await http.get(`/onyomi/reading/${encodeURIComponent(reading)}`, {
    params: { group_by_phonetic },
  })).data;

export const fetchOnyomiKanji = async (ch, group_by_phonetic = true) =>
  (await http.get(`/onyomi/kanji/${encodeURIComponent(ch)}`, {
    params: { group_by_phonetic },
  })).data;

export const fetchOnyomiReadings = async (min_count = 4) =>
  (await http.get("/onyomi/readings", { params: { min_count } })).data;

export const fetchWord = async (word) =>
  (await http.get(`/word/${encodeURIComponent(word)}`)).data;

export const fetchSearch = async (q) =>
  (await http.get(`/search`, { params: { q } })).data;

export const fetchPhoneticTree = async (ch) =>
  (await http.get(`/phonetic-tree/${encodeURIComponent(ch)}`)).data;

export const fetchVocabByKanji = async (ch, limit = 40) =>
  (await http.get(`/vocab/by-kanji/${encodeURIComponent(ch)}`, { params: { limit } })).data;

// progress
export const fetchProgress = async () => (await http.get("/progress")).data;
export const setProgress = async (ch, status) =>
  (await http.post(`/progress/${encodeURIComponent(ch)}`, { status })).data;
export const fetchEntitlement = async () =>
  (await http.get("/entitlement")).data;

export const createCheckoutSession = async (origin_url, package_id = "lifetime") =>
  (await http.post("/checkout/session", { origin_url, package_id })).data;

export const fetchCheckoutStatus = async (session_id) =>
  (await http.get(`/checkout/status/${encodeURIComponent(session_id)}`)).data;

export const fetchUnlockedWords = async (limit = 60) =>
  (await http.get("/unlocked-words", { params: { limit } })).data;
export const fetchUnlockedKanji = async (limit = 80) =>
  (await http.get("/unlocked-kanji", { params: { limit } })).data;
export const fetchStudyDeck = async (limit = 20) =>
  (await http.get("/study-deck", { params: { limit } })).data;
export const fetchWordProgress = async () => (await http.get("/word-progress")).data;
export const setWordProgress = async (word, status) =>
  (await http.post(`/word-progress/${encodeURIComponent(word)}`, { status })).data;

// mnemonic
export const fetchMnemonic = async (ch) =>
  (await http.get(`/mnemonic/${encodeURIComponent(ch)}`)).data;
export const saveMnemonic = async (ch, text) =>
  (await http.post(`/mnemonic/${encodeURIComponent(ch)}`, { text })).data;
export const deleteMnemonic = async (ch) =>
  (await http.delete(`/mnemonic/${encodeURIComponent(ch)}`)).data;
